import React, { useCallback, useRef, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import DeleteIcon from "@mui/icons-material/Delete";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItem from "@mui/material/ListItem";
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import ThemedTypography from "./ThemedTypography";
import ThemedIconButton from "./ThemedIconButton";
import MobileEditContactDialog from "./dialogs/MobileEditContactDialog";
import { decoratedFetch } from "../req_utils";
import {
  DARK_THEME_INPUT_OUTLINE_COLOR,
  DARK_THEME_TEXT_COLOR,
  DARK_THEME_DISABLED_TEXT_COLOR,
  GENESIS_LOGO_COLOR,
} from "../constants";

const PROGRESS_INCREMENT = 2;
const PROGRESS_TICK = 30;

const MobileContactsTab = ({ open, accountId, setSnackbarMessage }) => {
  const darkThemeEnabled = useSelector((state) => state.darkThemeEnabled);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [selectedListItemIdx, setSelectedListItemIdx] = useState(-1);
  const [dialogState, setDialogState] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const timerRef = useRef(null);
  const contactAlreadyDeleted = useRef(false);
  const [backdropOpen, setBackdropOpen] = useState(false);

  const handleDeleteContact = (listItemIdx) => {
    decoratedFetch(`/delete_contact/${contacts[listItemIdx].id}`, {
      method: "DELETE",
    })
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Unknown error");
        }
        return response.json();
      })
      .then((data) => {
        setContacts(contacts.filter((contact, i) => i !== listItemIdx));
        setSnackbarMessage("Contact deleted");
      })
      .catch((error) => {
        console.error(`Error deleting contact: ${error}`);
        setSnackbarMessage("An error occurred when deleting contact");
      });
  };

  const reset = useCallback(() => {
    setSelectedListItemIdx(-1);
    clearInterval(timerRef.current);
    timerRef.current = null;
    setProgress(0);
    setTimeout(() => (contactAlreadyDeleted.current = false), 100);
  }, []);

  const start = useCallback(
    (listItemIdx) => {
      timerRef.current = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress >= 100) {
            if (!contactAlreadyDeleted.current) {
              contactAlreadyDeleted.current = true;
              handleDeleteContact(listItemIdx);
              reset();
            }
            return 0;
          }
          return prevProgress + PROGRESS_INCREMENT;
        });
      }, PROGRESS_TICK);
    },
    [reset, handleDeleteContact],
  );

  const handlePressStart = (listItemIdx) => {
    setSelectedListItemIdx(listItemIdx);
    if (!timerRef.current) start(listItemIdx);
  };

  const handlePressEnd = () => {
    if (!contactAlreadyDeleted.current) reset();
  };

  const listContacts = () => {
    if (!accountId) {
      setContacts([]);
      setLoading(false);
      return;
    }

    decoratedFetch(`/list_contacts?account_id=${accountId}`)
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Unknown error");
        }
        return response.json();
      })
      .then((data) => {
        setContacts(data);
        setLoading(false);
        setBackdropOpen(false);
      })
      .catch((error) => {
        setSnackbarMessage("Failed to load contacts");
        setLoading(false);
        setBackdropOpen(false);
        console.error(`Error listing contacts: ${error}`);
      });
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    listContacts();
  }, [open, accountId]);

  const handleAddContact = () => {
    setCreateDialogOpen(true);
  };

  const handleEditContact = (contact) => {
    setDialogState({ selectedContact: contact });
  };

  const handleDialogClose = () => {
    setDialogState(null);
  };

  const handleCreateDialogClose = () => {
    setCreateDialogOpen(false);
  };

  const handleCreateContact = (newContact) => {
    setContacts((prevContacts) => [...prevContacts, newContact]);
    setCreateDialogOpen(false);
    setTimeout(() => setDialogState({ selectedContact: newContact }), 200);
  };

  const handleContactUpdate = (updatedContact) => {
    setContacts((prevContacts) =>
      prevContacts.map((contact) =>
        contact.id === updatedContact.id
          ? {
              ...contact,
              contact_type: updatedContact.contact_type,
              first_name: updatedContact.first_name,
              last_name: updatedContact.last_name,
              cell_no: updatedContact.cell_no,
              email: updatedContact.email,
            }
          : contact,
      ),
    );
  };

  return (
    <Box sx={{ height: "100%", position: "relative" }}>
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={backdropOpen}
        onClick={() => setBackdropOpen(false)}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <MobileEditContactDialog
        parentState={dialogState}
        createDialogOpen={createDialogOpen}
        handleClose={handleDialogClose}
        handleCreateClose={handleCreateDialogClose}
        onContactUpdate={handleContactUpdate}
        onCreateContact={handleCreateContact}
        accountId={accountId}
        setSnackbarMessage={setSnackbarMessage}
      />
      {contacts?.length ? (
        <List sx={{ py: 0 }}>
          {contacts.map((contact, index) => (
            <React.Fragment key={index}>
              <ListItem sx={{ p: 0 }}>
                <Grid container>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <ListItemButton
                      key={index}
                      onClick={() => handleEditContact(contact)}
                    >
                      <ListItemText
                        primary={`${contact.first_name} ${contact.last_name}`}
                        secondary={
                          <>
                            <p style={{ marginTop: "4px", marginBottom: 0 }}>
                              <strong>{"Type: "}</strong>
                              {contact?.contact_type}
                            </p>
                            <p style={{ marginTop: "4px", marginBottom: 0 }}>
                              <strong>{"Email: "}</strong>
                              {contact?.email || "None"}
                            </p>
                            <p style={{ marginTop: "4px", marginBottom: 0 }}>
                              <strong>{"Cell: "}</strong>
                              {contact?.cell_no || "None"}
                            </p>
                          </>
                        }
                        primaryTypographyProps={{
                          sx: {
                            fontWeight: "bold",
                            color: darkThemeEnabled
                              ? DARK_THEME_TEXT_COLOR
                              : "inherit",
                          },
                        }}
                        secondaryTypographyProps={{
                          sx: {
                            color: darkThemeEnabled
                              ? DARK_THEME_DISABLED_TEXT_COLOR
                              : "inherit",
                          },
                        }}
                      />
                    </ListItemButton>
                    <ThemedIconButton
                      onMouseDown={() => handlePressStart(index)}
                      onTouchStart={() => handlePressStart(index)}
                      onMouseUp={() => handlePressEnd()}
                      onTouchEnd={() => handlePressEnd()}
                      sx={{
                        width: "54px",
                        height: "54px",
                        "& svg": {
                          fontSize: "24px",
                        },
                        ml: "5px",
                        mr: "10px",
                      }}
                    >
                      <DeleteIcon />
                    </ThemedIconButton>
                  </Grid>
                  {progress > 0 && selectedListItemIdx === index && (
                    <Grid item xs={12} sx={{ mt: "-4px" }}>
                      <LinearProgress variant="determinate" value={progress} />
                    </Grid>
                  )}
                </Grid>
              </ListItem>
              {index < contacts.length - 1 && (
                <Divider
                  component="li"
                  sx={{
                    backgroundColor: darkThemeEnabled
                      ? DARK_THEME_INPUT_OUTLINE_COLOR
                      : "inherited",
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </List>
      ) : loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <CircularProgress size="50px" />
        </Box>
      ) : backdropOpen ? null : !accountId ? (
        <ThemedTypography
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          Save account first to add contacts
        </ThemedTypography>
      ) : (
        <ThemedTypography
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          No contacts
        </ThemedTypography>
      )}
      {accountId && (
        <Fab
          color="primary"
          aria-label="add"
          size="medium"
          sx={{ position: "fixed", bottom: 20, right: 20 }}
          onClick={handleAddContact}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
};

export default MobileContactsTab;
